/**
 * Following a conversation to the bottom, without fighting the reader.
 *
 * The page scrolls the window rather than an inner container — the header is
 * in normal flow, the composer is `position: sticky`, and a phone's software
 * keyboard behaves far better against a document scroll than against an
 * `overflow: auto` box. Sticky elements stay in flow, so the bottom of the
 * document is still the honest bottom of the conversation.
 *
 * What this replaces was one `scrollIntoView` keyed on `[messages.length,
 * streaming, phase]`, and it had three faults worth naming so they do not come
 * back:
 *
 *   · `streaming` changes once per token, so every delta cancelled the
 *     in-flight smooth scroll and started a new one. The easing restarts at
 *     zero each time, so the page never arrived anywhere.
 *   · There was no "are they already at the bottom?" test, so scrolling up to
 *     re-read an earlier answer was undone by the next token.
 *   · A dep array only sees the values in it. A bubble reflowing as its text
 *     wraps, the error block appearing and the composer growing all move the
 *     bottom without changing any dep, so none of them were followed. A
 *     ResizeObserver on the column sees all four.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Roughly three lines of assistant text. Inside this, a token landing while
 * they read still counts as being at the bottom; past it, they went looking
 * for something and should be left alone. Sub-pixel layout and browser zoom
 * need more than a pixel or two of tolerance on their own.
 */
export const AT_BOTTOM_SLACK = 96;

/** Pure, so the threshold is testable without a DOM. */
export const isNearBottom = (
  scrollHeight: number,
  viewport: number,
  scrollY: number,
  slack: number = AT_BOTTOM_SLACK
): boolean => scrollHeight - viewport - scrollY <= slack;

const documentBottom = (): number => document.documentElement.scrollHeight;

/**
 * Keeps the window pinned to the bottom while the reader is there, and reports
 * when they are not so a "jump to latest" can be offered.
 *
 * `contentRef` is the growing column — messages and composer both, so a
 * composer that grows with a long answer is followed like anything else.
 */
export function useStickToBottom(
  enabled: boolean,
  contentRef: React.RefObject<HTMLElement>
): { atBottom: boolean; jump: () => void; follow: () => void } {
  const [atBottom, setAtBottom] = useState(true);
  const pinned = useRef(true);
  const frame = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    const read = () => {
      frame.current = 0;
      const near = isNearBottom(documentBottom(), window.innerHeight, window.scrollY);
      pinned.current = near;
      setAtBottom(near);
    };

    // One passive listener, coalesced to a frame. The callback never writes
    // layout, so a streaming turn costs one measurement per frame rather than
    // one per token.
    const onScroll = () => {
      if (frame.current) return;
      frame.current = window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // A software keyboard shrinks the visual viewport rather than scrolling
    // the page. Without this, focusing the composer on a phone reads as the
    // reader having scrolled away, and the follow stops for the rest of the
    // conversation.
    window.visualViewport?.addEventListener('resize', onScroll);

    return () => {
      if (frame.current) window.cancelAnimationFrame(frame.current);
      frame.current = 0;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.visualViewport?.removeEventListener('resize', onScroll);
    };
  }, [enabled]);

  /**
   * Go to the bottom, but only if that is where they already were.
   *
   * Instant, not smooth. At token rate a three-pixel step is
   * indistinguishable from an animated one, and — the fault this replaces —
   * a smooth scroll restarted every token never finishes its easing at all.
   * The scroll it causes measures a gap of about zero and re-pins itself, so
   * no "this one was ours" flag is needed.
   */
  const follow = useCallback(() => {
    if (pinned.current) window.scrollTo({ top: documentBottom(), behavior: 'auto' });
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return;

    // The observer is the half that catches what a dependency array cannot:
    // a bubble reflowing as its text wraps, the error block appearing, the
    // composer growing under a long answer. The caller drives `follow` on new
    // content as well, because observer delivery is tied to the rendering
    // steps and a tab that is not painting does not get any.
    const observer = new ResizeObserver(follow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, contentRef, follow]);

  const jump = useCallback(() => {
    pinned.current = true;
    setAtBottom(true);
    // The one place smoothness is worth it: they asked. Reduced motion turns
    // it back off through `scroll-behavior` in styles.css.
    window.scrollTo({ top: documentBottom(), behavior: 'smooth' });
  }, []);

  return { atBottom, jump, follow };
}
